import { Can } from '@/components/can/Can';
import { Form, useForm } from '@/components/forms';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation } from '@/hoslog-intl';
import type { FileObject } from '@/lib/uploadToAWS';
import { uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { HostManagement } from '@/services/hostManagement/hostManagement.types';
import { hostFormSchema } from '../../basic-stores/schema/host-form.schema';
import { buildHostPayload } from '../../basic-stores/utils/buildHostPayload';
import { PersonalInfo } from '../../PersonalInfo';
import { SkillsSet } from '../../SkillsSet';
import { SNS } from '../../Sns';
import { useAvailableRankOptions } from '../hooks/useAvailableRankOptions';
import useManageHostInfo from '../hooks/useManageHostInfo';

export type HostFormValues = Omit<HostManagement, 'profileImage'> & {
  profileImage?: FileObject | File | string | null;
};

export const ManageHost = ({
  defaultValues,
  hostCodeParam,
}: {
  defaultValues: HostFormValues;
  hostCodeParam: string;
}) => {
  const { mutateAsync } = useManageHostInfo();

  const form = useForm({
    schema: hostFormSchema,
    mode: 'onBlur',
    defaultValues: defaultValues,
  });

  const rankOptions = useAvailableRankOptions(defaultValues?.clubId, defaultValues?.hostCode || hostCodeParam);
  const { getValues } = form;
  const handleSubmit = async () => {
    const formData = getValues();
    let profileImageUrl = '';
    const profileImage = formData.profileImage as FileObject | string | null | undefined;

    if (typeof profileImage === 'string') {
      profileImageUrl = profileImage;
    } else if (
      profileImage &&
      typeof profileImage === 'object' &&
      'file' in profileImage &&
      profileImage.file instanceof File
    ) {
      profileImageUrl = await uploadFileToS3WithPresignedUrl(profileImage);
    }

    const payload = buildHostPayload({
      clubId: defaultValues?.clubId || '',
      hostId: defaultValues?.hostId || '',
      hostCode: defaultValues?.hostCode || hostCodeParam || '',
      formData,
      profileImageUrl,
    });
    await mutateAsync(payload);
  };

  return (
    <div className="rounded-lg bg-card shadow-panel p-8">
      <Form form={form} onSubmit={handleSubmit}>
        <PersonalInfo hostDetails={defaultValues} rankOptions={rankOptions} />
        <SNS storeOrHost="host" />
        <SkillsSet hostDetails={defaultValues} />
        <div className="border-t-2 border-t-muted pt-6 flex flex-row-reverse w-full gap-4 text-sm mt-6 justify-between">
          <Can rule={{ allOf: [PERMS.host.premium.update] }}>
            <Button type="submit" className="w-[110px]">
              <Translation id="common.page.button.submit" />
            </Button>
          </Can>
          <Button type="button" variant="outline" className="w-[122px]" onClick={() => window.history.back()}>
            <Translation id="common.page.button.cancel" />
          </Button>
        </div>
      </Form>
    </div>
  );
};
